import React, { useState } from "react";
import { toast } from "sonner";
import { useWallet } from "@/context/WalletContext";
import { usePolling } from "@/components/croc/hooks";
import { Panel, Pill, StatCard } from "@/components/croc/ui";
import { fmtUsd, fmtNum, shortAddr } from "@/lib/format";
import api from "@/lib/api";
import { Sparkles, BadgePercent, Coins, ArrowDownToLine, Wallet } from "lucide-react";

export const MarketingPoints = () => {
  const { address, signAction } = useWallet();
  const { data, reload } = usePolling(() => api.mktUser(address || "0x0"), [address], 20000);
  const [amt, setAmt] = useState("");
  const [busy, setBusy] = useState(false);
  const accrued = data?.accrued_usdt ?? 0;
  const enabled = data?.enabled !== false;
  const pct = data?.pct;

  const withdraw = async () => {
    const a = parseFloat(amt) || 0;
    if (a <= 0) return toast.error("Enter an amount");
    try {
      setBusy(true);
      const sig = await signAction("mkt_withdraw");
      await api.mktWithdraw({ address, amount: a, ...sig });
      toast.success(`${fmtUsd(a)} credited instantly`, { description: "Added to your withdrawable balance" });
      setAmt("");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Withdraw failed");
    } finally { setBusy(false); }
  };

  return (
    <Panel glow="gold" data-testid="marketing-points">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 gold-text" />
          <span className="font-display font-bold text-white">Marketing Points</span>
        </div>
        <Pill tone={enabled ? "emerald" : "danger"} data-testid="mkt-feature-pill">
          {enabled ? "Active" : "Paused by admin"}
        </Pill>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <StatCard label="Points Balance" value={fmtNum(data?.points_balance ?? 0, 2)} icon={Sparkles} accent="gold" testid="mkt-points-balance" />
        <StatCard label="Your Redeem Rate" value={pct ? `${Math.round(pct * 100)}%` : "—"} icon={BadgePercent} accent="emerald" testid="mkt-pct" />
        <StatCard label="Accrued (USDT)" value={fmtUsd(accrued)} icon={Coins} accent="emerald" testid="mkt-accrued" />
        <StatCard label="Withdrawn" value={fmtUsd(data?.withdrawn_usdt)} icon={Wallet} accent="gold" testid="mkt-withdrawn" />
      </div>

      <div className="rounded-xl border border-white/10 bg-[#090E0B] p-3.5">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 flex-1 rounded-lg border border-white/10 bg-[#0B120E] px-3 py-2.5">
            <span className="text-muted-foreground text-sm">$</span>
            <input data-testid="mkt-withdraw-amount" type="number" value={amt} onChange={(e) => setAmt(e.target.value)}
              placeholder="0.00" className="bg-transparent font-num font-bold text-white w-full outline-none" />
            <button data-testid="mkt-withdraw-max" onClick={() => setAmt(String(accrued))}
              className="text-[10px] uppercase tracking-widest gold-text hover:text-white">Max</button>
          </div>
          <button data-testid="mkt-withdraw-button" onClick={withdraw} disabled={busy || !enabled || accrued <= 0}
            className="flex items-center gap-2 rounded-xl bg-[#1CFD4C] px-5 py-2.5 font-semibold text-[#050806] hover:bg-[#4DFF74] transition-colors disabled:opacity-50">
            <ArrowDownToLine className="w-4 h-4" /> Withdraw
          </button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-2">
          When business lands in your downline, {pct ? `${Math.round(pct * 100)}%` : "your %"} of your point balance converts to USDT (1 pt = $1) — paid from the admin marketing budget.
        </p>
      </div>

      {(data?.recent_redemptions?.length ?? 0) > 0 && (
        <div className="mt-4">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Recent Redemptions</div>
          <div className="space-y-1.5 max-h-[180px] overflow-y-auto pr-1">
            {data.recent_redemptions.map((r, i) => (
              <div key={r.id || i} className="flex items-center justify-between rounded-lg border border-white/10 bg-[#090E0B] px-3 py-2" data-testid={`mkt-redemption-${i}`}>
                <span className="font-num text-xs text-muted-foreground">from {shortAddr(r.source)}</span>
                <span className="font-num text-xs text-white">{Math.round((r.eff_pct || 0) * 100)}%</span>
                <span className="font-num text-xs emerald-text">+{fmtUsd(r.usd)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Panel>
  );
};
